import React, { useState } from "react";
import { useUser, SignOutButton, useClerk, useAuth } from "@clerk/clerk-react";
import { useCommonTranslation } from "@tablemint/i18n";
import AnimatedSection from "../components/animations/AnimatedSection";
import axios from "axios";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { RefreshCw, Check, ArrowUpDown, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
// @ts-ignore - Badge component exists but TypeScript is not recognizing it
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

// Define TypeScript interfaces for subscription data
interface Subscription {
  id: string;
  status: string;
  plan: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

// Define available plans
const AVAILABLE_PLANS = ["STARTER", "GROWTH", "PREMIUM"];

const Dashboard: React.FC = () => {
  const { t } = useCommonTranslation();
  const { user } = useUser();
  const { session } = useClerk();
  const { isSignedIn, isLoaded } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State for modals
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Use TanStack Query to fetch subscription data
  const {
    data: subscription,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      try {
        setError(null);

        // Get session token for API authentication
        if (!session) {
          console.error("No active Clerk session found");
          throw new Error("No active session - please login again");
        }

        // Try to get a fresh token
        const token = await session.getToken();

        // Verify we have a well-formed token
        if (!token) {
          console.error("Failed to get token from Clerk session");
          throw new Error("Authentication token not available");
        }

        // Call backend API to get subscription info
        console.log("Making API request to /api/subscriptions/current");
        const response = await axios.get("/api/subscriptions/current", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log("Subscription API response:", response.status);
        return response.data;
      } catch (err: any) {
        console.error("Error fetching subscription:", err);

        // Enhanced error logging
        if (err.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error("Response status:", err.response.status);
          console.error("Response data:", err.response.data);
          console.error("Response headers:", err.response.headers);
          setError(
            `Error ${err.response.status}: ${JSON.stringify(err.response.data)}`
          );
        } else if (err.request) {
          // The request was made but no response was received
          console.error("No response received");
          setError("No response received from server");
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error("Request error:", err.message);
          setError(`Request error: ${err.message}`);
        }
        throw err;
      }
    },
    enabled: isSignedIn === true && !!session,
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      setIsProcessing(true);
      await axios.post("/api/subscriptions/cancel");
    },
    onSuccess: () => {
      setCancelDialogOpen(false);
      setIsProcessing(false);
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
    },
    onError: (error) => {
      console.error("Error canceling subscription:", error);
      setError("Failed to cancel subscription");
      setIsProcessing(false);
    },
  });

  const changePlanMutation = useMutation({
    mutationFn: async (newPlan: string) => {
      setIsProcessing(true);
      await axios.post("/api/subscriptions/change-plan", { plan: newPlan });
    },
    onSuccess: () => {
      setChangePlanDialogOpen(false);
      setIsProcessing(false);
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
    },
    onError: (error) => {
      console.error("Error changing subscription plan:", error);
      setError("Failed to change subscription plan");
      setIsProcessing(false);
    },
  });

  const syncSubscriptionMutation = useMutation({
    mutationFn: async () => {
      try {
        const token = await session?.getToken();
        console.log(
          "Syncing subscriptions with email:",
          user?.primaryEmailAddress?.emailAddress
        );

        const response = await axios.post(
          "/api/subscriptions/sync-subscriptions",
          {
            clerkEmail: user?.primaryEmailAddress?.emailAddress,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("Subscription sync response:", response.data);
        return response.data;
      } catch (error) {
        console.error("Error in sync subscription mutation:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      console.log("Subscription sync success data:", data);
      toast.success("Subscription synchronized successfully");
    },
    onError: (error) => {
      console.error("Error syncing subscriptions:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("Error response:", error.response.data);
        toast.error(
          `Failed to sync subscription: ${error.response.data.message || error.message}`
        );
      } else {
        toast.error(
          `Failed to sync subscription: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    },
  });

  const handleCancelSubscription = () => {
    cancelSubscriptionMutation.mutate();
  };

  const handleChangePlan = () => {
    if (selectedPlan) {
      changePlanMutation.mutate(selectedPlan);
    }
  };

  const syncSubscriptions = () => {
    syncSubscriptionMutation.mutate();
  };

  // Function to get plan features based on plan name
  const getPlanFeatures = (planName: string) => {
    const features = [
      {
        name: t("features.basicReservations"),
        starter: true,
        growth: true,
        premium: true,
      },
      {
        name: t("features.customBranding"),
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: t("features.analytics"),
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: t("features.multiLocation"),
        starter: false,
        growth: false,
        premium: true,
      },
    ];

    return features.map((feature) => ({
      name: feature.name,
      included:
        feature[planName.toLowerCase() as "starter" | "growth" | "premium"],
    }));
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium">Loading...</h2>
          <p className="mt-2">Please wait while we load your dashboard.</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate("/sign-in");
    return null;
  }

  return (
    <div className="min-h-screen bg-background pt-16">
      <AnimatedSection className="container mx-auto px-4 py-12">
        <div className="bg-card rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-2xl font-bold text-foreground">
              {t("dashboard.welcome", {
                name: user?.firstName || user?.username,
              })}
            </h1>
            <SignOutButton>
              <Button variant="outline">{t("auth.signout")}</Button>
            </SignOutButton>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                {t("dashboard.accountInfo")}
              </h2>
              <div className="space-y-2">
                <p>
                  <span className="font-medium">{t("dashboard.email")}:</span>{" "}
                  {user?.primaryEmailAddress?.emailAddress}
                </p>
                <p>
                  <span className="font-medium">{t("dashboard.name")}:</span>{" "}
                  {user?.fullName || user?.username}
                </p>
              </div>
            </div>

            <div className="bg-background p-6 rounded-lg shadow-sm border border-border">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                {t("dashboard.quickActions")}
              </h2>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start text-primary"
                  onClick={() => navigate("/reservations")}
                >
                  {t("dashboard.viewReservations")}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start text-primary"
                  onClick={() => navigate("/settings")}
                >
                  {t("dashboard.manageSettings")}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start text-primary"
                  onClick={() => navigate("/widget-configurator")}
                >
                  {t("dashboard.configureWidget")}
                </Button>
              </div>
            </div>
          </div>

          {/* Subscription Section */}
          <div className="mt-8 bg-background p-6 rounded-lg shadow-sm border border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                {t("dashboard.subscriptionInfo")}
              </h2>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                />
                <span>{t("common.refresh")}</span>
              </Button>
            </div>

            {isLoading && (
              <div className="text-center py-4">
                <p>{t("dashboard.loadingSubscription")}</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
                <p>{error}</p>
              </div>
            )}

            {!isLoading && !error && subscription ? (
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-muted/20 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg font-semibold">
                        {subscription.plan}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          subscription.status === "ACTIVE"
                            ? "bg-green-100 text-green-800"
                            : subscription.status === "CANCELED"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {subscription.status}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(
                        subscription.currentPeriodEnd
                      ).toLocaleDateString()}
                    </p>
                    {subscription.cancelAtPeriodEnd && (
                      <p className="text-sm text-red-600 mt-1">
                        {t("dashboard.autoRenew")}:{" "}
                        {subscription.cancelAtPeriodEnd
                          ? t("common.no")
                          : t("common.yes")}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {subscription.status === "ACTIVE" && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedPlan(null);
                            setChangePlanDialogOpen(true);
                          }}
                        >
                          <ArrowUpDown className="h-4 w-4 mr-2" />
                          {t("dashboard.changePlan")}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => setCancelDialogOpen(true)}
                        >
                          {t("dashboard.cancelSubscription")}
                        </Button>
                      </>
                    )}
                    {subscription.status === "CANCELED" && (
                      <Button
                        variant="default"
                        onClick={() => navigate("/pricing")}
                      >
                        {t("dashboard.renewSubscription")}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="text-md font-medium mb-4">
                    {t("dashboard.includedFeatures")}:
                  </h3>
                  <ul className="space-y-2 mt-2">
                    {getPlanFeatures(subscription.plan).map(
                      (feature, index) => (
                        <li key={index} className="flex items-start">
                          {feature.included ? (
                            <Check className="text-green-500 h-5 w-5 mr-2 shrink-0" />
                          ) : (
                            <span className="text-muted-foreground h-5 w-5 mr-2 flex items-center justify-center">
                              -
                            </span>
                          )}
                          <span
                            className={
                              feature.included ? "" : "text-muted-foreground"
                            }
                          >
                            {feature.name}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            ) : !isLoading && !error ? (
              <div className="text-center py-6">
                <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-lg mb-4">{t("dashboard.noSubscription")}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                  <Button
                    variant="default"
                    onClick={() => navigate("/pricing")}
                  >
                    {t("dashboard.viewPlans")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={syncSubscriptions}
                    disabled={isFetching}
                  >
                    {isFetching
                      ? t("common.syncing")
                      : t("common.syncFromStripe")}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          {/* Debug section */}
          <div className="mt-8 p-4 bg-muted/30 rounded-lg border border-border">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              {t("dashboard.debugInfo")}
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-2 px-4 font-medium">User ID</td>
                    <td className="py-2 px-4">{user?.id}</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 px-4 font-medium">Email Verified</td>
                    <td className="py-2 px-4">
                      {user?.primaryEmailAddress?.verification?.status ||
                        "Unknown"}
                    </td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 px-4 font-medium">Created At</td>
                    <td className="py-2 px-4">
                      {user?.createdAt
                        ? new Date(user.createdAt).toLocaleString()
                        : "Unknown"}
                    </td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 px-4 font-medium">Last Sign In</td>
                    <td className="py-2 px-4">
                      {user?.lastSignInAt
                        ? new Date(user.lastSignInAt).toLocaleString()
                        : "Unknown"}
                    </td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 px-4 font-medium">Session ID</td>
                    <td className="py-2 px-4">{session?.id || "No session"}</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-4 font-medium">Auth Status</td>
                    <td className="py-2 px-4">
                      {isLoaded
                        ? isSignedIn
                          ? "Signed In"
                          : "Not Signed In"
                        : "Loading..."}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const token = await session?.getToken();
                    console.log(
                      "Syncing with email:",
                      user?.primaryEmailAddress?.emailAddress
                    );
                    const response = await axios.post(
                      "/api/users/sync-with-clerk",
                      {
                        email: user?.primaryEmailAddress?.emailAddress,
                        firstName: user?.firstName,
                        lastName: user?.lastName,
                        clerkId: user?.id,
                      },
                      {
                        headers: {
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );
                    console.log("Sync response:", response.data);
                    toast.success("User data synchronized successfully");
                    // Refresh subscription data after syncing user
                    syncSubscriptions();
                  } catch (error) {
                    console.error("Error syncing user data:", error);
                    toast.error("Failed to sync user data");
                  }
                }}
              >
                Sync User Data with Clerk
              </Button>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("dialog.cancelSubscription")}</DialogTitle>
            <DialogDescription>
              {t("dialog.cancelSubscriptionDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center p-3 bg-yellow-50 text-yellow-800 rounded-md">
              <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
              <p className="text-sm">{t("dialog.cancelWarning")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={isProcessing}
            >
              {t("dialog.goBack")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={isProcessing}
            >
              {isProcessing
                ? t("dialog.processing")
                : t("dialog.confirmCancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog
        open={changePlanDialogOpen}
        onOpenChange={setChangePlanDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("dialog.changePlan")}</DialogTitle>
            <DialogDescription>
              {t("dialog.changePlanDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {AVAILABLE_PLANS.map((plan) => (
              <div
                key={plan}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedPlan === plan
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30"
                }`}
                onClick={() => setSelectedPlan(plan)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{plan}</h3>
                    <p className="text-sm text-muted-foreground">
                      {plan === "STARTER"
                        ? t("plans.starterDesc")
                        : plan === "GROWTH"
                          ? t("plans.growthDesc")
                          : t("plans.premiumDesc")}
                    </p>
                  </div>
                  {subscription?.plan === plan && (
                    <span className="text-sm text-primary font-medium">
                      {t("dialog.currentPlan")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChangePlanDialogOpen(false)}
              disabled={isProcessing}
            >
              {t("dialog.cancel")}
            </Button>
            <Button
              variant="default"
              onClick={handleChangePlan}
              disabled={
                isProcessing ||
                !selectedPlan ||
                selectedPlan === subscription?.plan
              }
            >
              {isProcessing
                ? t("dialog.processing")
                : selectedPlan === subscription?.plan
                  ? t("dialog.currentPlanSelected")
                  : t("dialog.confirmChangePlan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
